import { getRecoil, setRecoil, resetRecoil } from 'recoil-nexus';

import * as auth from '@/api/auth.service';
import * as profile from '@/api/profile.service';
import {
  getAuthToken,
  setAuthToken,
  setExplicitLogout,
  hasExplicitLogout,
} from '@/api/auth-token';
import { mapToIUser, mapPendingPopups } from '@/api/user-mapper';

import {
  SignupValuesType,
  LoginValuesType,
  ChangePasswordType,
  EditAccountValuesType,
} from './types';

import * as userSelector from './userSelector';
import { pendingPopupsState } from './pendingPopupsState';
import { executeTurnstile } from '@/utils/turnstile';

export const applyUserPayload = (raw: unknown) => {
  setRecoil(userSelector.user, mapToIUser(raw));
  setRecoil(pendingPopupsState, mapPendingPopups(raw));
};

export const loadUser = async () => {
  const { data, pendingPopups } = await profile.getUser();
  setRecoil(userSelector.user, data);
  setRecoil(pendingPopupsState, pendingPopups);
};

export const logout = () => {
  const verified = getRecoil(userSelector.user)?.emailVerified;
  if (verified) {
    setExplicitLogout(true);
  }
  setAuthToken(null);
  resetRecoil(userSelector.user);
  resetRecoil(pendingPopupsState);
  auth.logout().catch(() => {});
};

export const login = async (values: LoginValuesType) => {
  try {
    setExplicitLogout(false);
    const payload = (await auth.login(values)) as {
      token?: string;
      user?: unknown;
    };
    if (payload.token) {
      setAuthToken(payload.token);
      await loadUser();
    } else if (payload.user) {
      applyUserPayload(payload.user);
    }
  } catch (err) {
    throw err;
  }
};

export const checkLogin = async () => {
  const token = getAuthToken();
  if (token) {
    try {
      await loadUser();
      return;
    } catch {
      setAuthToken(null);
    }
  }

  if (hasExplicitLogout()) {
    return;
  }

  try {
    const turnstile = await executeTurnstile();
    const payload = (await auth.createGuest(turnstile)) as {
      token?: string;
      user?: unknown;
    };
    if (payload.token) {
      setAuthToken(payload.token);
      await loadUser();
    } else if (payload.user) {
      applyUserPayload(payload.user);
    }
  } catch {
    // pid-only play
  }
};

export const signup = async (values: SignupValuesType) => {
  try {
    setExplicitLogout(false);
    const payload = (await auth.signup(values)) as {
      token?: string;
      user?: unknown;
    };
    if (payload.token) {
      setAuthToken(payload.token);
      await loadUser();
    } else if (payload.user) {
      applyUserPayload(payload.user);
    }
  } catch (err) {
    throw err;
  }
};

export const resetPassword = async (values: ChangePasswordType) => {
  try {
    await profile.resetPassword(values);
    await loadUser();
  } catch (err) {
    throw err;
  }
};

export const updateProfile = async (values: EditAccountValuesType) => {
  try {
    await profile.updateProfile(values);
    await loadUser();
  } catch (err) {
    throw err;
  }
};

export const resendVerification = async () => {
  await auth.resendVerification();
};

export const completeTutorial = async () => {
  await profile.completeTutorial();
  await loadUser();
};
